import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { profilingEndpoints, ITestingProtocol } from "../../endpoints/profiling";

interface ConfiguraTestModalProps {
  open: boolean;
  onClose: () => void;
  athleteId: string;
}

/**
 * #5 ConfiguraTestModal — selezione esercizio ⭐ + protocollo + form input + submit compute.
 * MVP: supporto P1 VBT Lower (load_kg + velocity_ms incrementale).
 * Per altri protocolli MVP usa form generico (1 input riga per metrica).
 */
export const ConfiguraTestModal: React.FC<ConfiguraTestModalProps> = ({
  open,
  onClose,
  athleteId,
}) => {
  const qc = useQueryClient();
  const [selectedExercise, setSelectedExercise] = useState<string>("");
  const [selectedProtocol, setSelectedProtocol] = useState<string>("");
  const [inputs, setInputs] = useState<Array<Record<string, string>>>([
    { load_kg: "", velocity_ms: "" },
  ]);
  // Bivio VBT "Conosci l'1RM?" (spec sez.6.1.1): se valorizzato → MVT individuale
  const [known1RM, setKnown1RM] = useState<string>("");

  const { data: exResp } = useQuery({
    queryKey: ["recommended-exercises"],
    queryFn: () => profilingEndpoints.listRecommendedExercises().then((r) => r.data),
    enabled: open,
  });
  // Nuovo endpoint ritorna array diretto (non paginato)
  const exercises = Array.isArray(exResp) ? exResp : [];

  const { data: protoResp } = useQuery({
    queryKey: ["protocols-for-exercise", selectedExercise],
    queryFn: () =>
      profilingEndpoints
        .listProtocols({ exerciseId: selectedExercise })
        .then((r) => r.data),
    enabled: open && !!selectedExercise,
  });
  // Nuovo endpoint ritorna array diretto filtrato per exerciseId sul server
  const compatibleProtocols: ITestingProtocol[] = Array.isArray(protoResp) ? protoResp : [];

  const computeMut = useMutation({
    mutationFn: () => {
      const numericInputs = inputs.map((row) => {
        const r: any = {};
        for (const k of Object.keys(row)) r[k] = Number(row[k]);
        return r;
      });
      const known = Number(known1RM);
      return profilingEndpoints
        .computeProfile(selectedProtocol, {
          athleteId,
          sessionDate: new Date().toISOString(),
          inputs: numericInputs,
          // ramo "1RM noto" → backend deriva la MVT individuale (sez.6.1.1)
          ...(known1RM.trim() !== "" && known > 0 && { known1RM: known }),
        })
        .then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-profiles"] });
      onClose();
    },
  });

  const protocol = compatibleProtocols.find((p) => p._id === selectedProtocol);
  const metrics = protocol?.metrics ?? [];

  // Protocolli VBT (P1/P2) richiedono almeno 2 punti per la regressione lineare.
  // Per tutti gli altri protocolli è sufficiente 1 riga.
  const isVbt =
    protocol?.profilingHubProtocolId === "P1" ||
    protocol?.profilingHubProtocolId === "P2";
  const minPointsRequired = isVbt ? 2 : 1;
  const hasEnoughPoints = inputs.length >= minPointsRequired;

  if (!open) return null;

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="rgba(0,0,0,0.5)"
      zIndex={1000}
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={onClose}
    >
      <Box
        bg="white"
        borderRadius="lg"
        p={6}
        w="640px"
        maxH="90vh"
        overflowY="auto"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex justify="space-between" align="start" mb={4}>
          <Text fontWeight={700} fontSize="xl">Configura nuovo test</Text>
          <Button size="sm" variant="ghost" onClick={onClose}>✕</Button>
        </Flex>

        <Stack gap={4}>
          <Box>
            <Text fontSize="sm" fontWeight={600} mb={1}>1. Esercizio (⭐ raccomandati testing)</Text>
            <select
              value={selectedExercise}
              onChange={(e) => { setSelectedExercise(e.target.value); setSelectedProtocol(""); }}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14, background: "white" }}
            >
              <option value="">— Scegli esercizio —</option>
              {exercises.map((e) => (
                <option key={e._id} value={e._id}>⭐ {e.name?.it || e.name?.en}</option>
              ))}
            </select>
          </Box>

          {selectedExercise && (
            <Box>
              <Text fontSize="sm" fontWeight={600} mb={1}>
                2. Protocollo ({compatibleProtocols.length} disponibili per questo esercizio)
              </Text>
              {compatibleProtocols.length === 0 ? (
                <Text fontSize="xs" color="gray.500">Nessun protocollo collegato. Seleziona altro esercizio.</Text>
              ) : (
                <Stack gap={2}>
                  {compatibleProtocols.map((p) => (
                    <Box
                      key={p._id}
                      onClick={() => {
                        setSelectedProtocol(p._id);
                        // Pulisce le chiavi stale: ricrea inputs con le metriche del nuovo protocollo
                        const newEmpty: Record<string, string> = {};
                        (p.metrics ?? []).forEach((m: any) => { newEmpty[m.key] = ""; });
                        setInputs([newEmpty]);
                      }}
                      cursor="pointer"
                      p={3}
                      border="1px solid"
                      borderColor={selectedProtocol === p._id ? "purple.500" : "gray.200"}
                      borderRadius="md"
                      bg={selectedProtocol === p._id ? "purple.50" : "white"}
                    >
                      <HStack>
                        {p.profilingHubProtocolId && (
                          <Box bg="purple.500" color="white" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="800" fontFamily="mono">
                            {p.profilingHubProtocolId}
                          </Box>
                        )}
                        <Text fontWeight={600}>{p.name}</Text>
                        <Text fontSize="xs" color="gray.500">({p.type})</Text>
                      </HStack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {protocol &&
            (protocol.profilingHubProtocolId === "P1" ||
              protocol.profilingHubProtocolId === "P2") && (
              <Box>
                <Text fontSize="sm" fontWeight={600} mb={1}>
                  Conosci l'1RM dell'atleta?{" "}
                  <Text as="span" fontSize="xs" color="gray.500">
                    (opzionale)
                  </Text>
                </Text>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Se lo inserisci, si deriva la MVT individuale (più accurata)
                  invece di stimare l'1RM alla MVT generica.
                </Text>
                <input
                  type="number"
                  inputMode="decimal"
                  value={known1RM}
                  onChange={(e) => setKnown1RM(e.target.value)}
                  placeholder="1RM noto (kg) — lascia vuoto per stimare"
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    fontSize: 14,
                    background: "white",
                  }}
                />
              </Box>
            )}

          {protocol && (
            <Box>
              <Text fontSize="sm" fontWeight={600} mb={2}>
                3. Inserisci dati ({metrics.length} metriche per riga)
              </Text>
              <Stack gap={2}>
                {inputs.map((row, idx) => (
                  <HStack key={idx} gap={2}>
                    <Text fontSize="xs" w="20px">#{idx + 1}</Text>
                    {metrics.map((m) => (
                      <Box key={m.key} flex={1}>
                        <Text fontSize="xs" color="gray.500">{m.label || m.key} ({m.unit})</Text>
                        <Input
                          size="sm"
                          type="number"
                          step="0.01"
                          value={row[m.key] || ""}
                          onChange={(e) => {
                            const next = [...inputs];
                            next[idx] = { ...next[idx], [m.key]: e.target.value };
                            setInputs(next);
                          }}
                        />
                      </Box>
                    ))}
                    {inputs.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => setInputs(inputs.filter((_, i) => i !== idx))}
                      >✕</Button>
                    )}
                  </HStack>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const empty: Record<string, string> = {};
                    metrics.forEach((m) => (empty[m.key] = ""));
                    setInputs([...inputs, empty]);
                  }}
                >
                  + Aggiungi riga
                </Button>
              </Stack>
            </Box>
          )}

          <Flex gap={2} justify="end" mt={4}>
            <Button variant="ghost" onClick={onClose}>Annulla</Button>
            <Button
              colorScheme="purple"
              disabled={!selectedProtocol || computeMut.isPending || !hasEnoughPoints}
              title={
                !hasEnoughPoints
                  ? `Servono almeno ${minPointsRequired} righe di dati per questo protocollo`
                  : undefined
              }
              onClick={() => computeMut.mutate()}
            >
              {computeMut.isPending ? <Spinner size="sm" /> : "Calcola profilo"}
            </Button>
          </Flex>
          {protocol && !hasEnoughPoints && (
            <Text color="orange.500" fontSize="xs" textAlign="right">
              Aggiungi almeno {minPointsRequired} righe di dati per calcolare il profilo.
            </Text>
          )}

          {computeMut.isError && (
            <Text color="red.500" fontSize="sm">
              Errore: {(computeMut.error as any)?.response?.data?.message || (computeMut.error as any)?.message}
            </Text>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default ConfiguraTestModal;
